import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';
import { ContactFormData, SocialLink } from '../types';
import { EMAILJS_CONFIG } from '../config/emailjs';

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactFormData>();

  const socialLinks: SocialLink[] = [
    { id: '1', name: 'Email', url: 'mailto:hodlwarden@gmail.com', icon: 'fas fa-envelope' },
    // { id: '2', name: 'LinkedIn', url: 'https://linkedin.com/in/hodlwarden', icon: 'fab fa-linkedin' },
    { id: '3', name: 'GitHub', url: 'https://github.com/hodlwarden', icon: 'fab fa-github' },
    { id: '4', name: 'Twitter', url: 'https://twitter.com/hodlwarden', icon: 'fab fa-twitter' },
    { id: '5', name: 'Discord', url: 'https://discord.gg/hodlwarden', icon: 'fab fa-discord' }
  ];

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Check if EmailJS is properly configured
      const isEmailJSConfigured = EMAILJS_CONFIG.serviceId !== 'service_portfolio' && 
                                   EMAILJS_CONFIG.templateId !== 'template_contact' && 
                                   EMAILJS_CONFIG.publicKey !== 'your_public_key';

      if (isEmailJSConfigured) {
        // Send email using EmailJS
        const templateParams = {
          from_name: data.name,
          from_email: data.email,
          subject: data.subject,
          message: data.message,
          to_email: EMAILJS_CONFIG.toEmail
        };

        await emailjs.send(
          EMAILJS_CONFIG.serviceId, 
          EMAILJS_CONFIG.templateId, 
          templateParams, 
          EMAILJS_CONFIG.publicKey
        );
        
      } else {
        // Fallback: Simulate email sending for development
        
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // In a real scenario, you might want to:
        // 1. Send to your backend API
        // 2. Use a different email service
        // 3. Store in a database for manual processing
      }
      
      setSubmitStatus('success');
      reset();
    } catch (error) {
      console.error('Email sending error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="contact" className="contact" ref={ref}>
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
        >
          Get In Touch
        </motion.h2>

        <motion.div
          className="contact-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div className="contact-info" variants={itemVariants}>
            <motion.h3
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              Let's Build the Future Together
              <i className="fas fa-rocket" style={{ marginLeft: '10px', color: '#00d4ff' }}></i>
            </motion.h3>
            
            <motion.p variants={itemVariants}>
              Ready to discuss your blockchain project? I'm always excited to work on innovative DeFi solutions and Web3 applications.
            </motion.p>

            <motion.div
              className="contact-methods"
              variants={containerVariants}
            >
              {socialLinks.map((link) => (
                <motion.a
                  key={link.id}
                  href={link.url}
                  className="contact-method"
                  variants={itemVariants}
                  whileHover={{ 
                    scale: 1.02,
                    backgroundColor: 'rgba(0, 212, 255, 0.1)',
                    borderColor: '#00d4ff'
                  }}
                  whileTap={{ scale: 0.98 }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className={link.icon} style={{ 
                    color: '#00d4ff', 
                    fontSize: '1.3rem',
                    transition: 'all 0.3s ease'
                  }}></i>
                  <span>{link.name}</span>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          <motion.div className="contact-form" variants={itemVariants}>
            <motion.form
              onSubmit={handleSubmit(onSubmit)}
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="form-group"
                variants={itemVariants}
              >
                <input
                  type="text"
                  placeholder="Your Name"
                  {...register('name', { required: 'Name is required' })}
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && (
                  <motion.span
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.name.message}
                  </motion.span>
                )}
              </motion.div>

              <motion.div
                className="form-group"
                variants={itemVariants}
              >
                <input
                  type="email"
                  placeholder="Your Email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && (
                  <motion.span
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.email.message}
                  </motion.span>
                )}
              </motion.div>

              <motion.div
                className="form-group"
                variants={itemVariants}
              >
                <input
                  type="text"
                  placeholder="Subject"
                  {...register('subject', { required: 'Subject is required' })}
                  className={errors.subject ? 'error' : ''}
                />
                {errors.subject && (
                  <motion.span
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.subject.message}
                  </motion.span>
                )}
              </motion.div>

              <motion.div
                className="form-group"
                variants={itemVariants}
              >
                <textarea
                  placeholder="Your Message"
                  rows={5}
                  {...register('message', { required: 'Message is required' })}
                  className={errors.message ? 'error' : ''}
                />
                {errors.message && (
                  <motion.span
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.message.message}
                  </motion.span>
                )}
              </motion.div>

              <motion.button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
                whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                variants={itemVariants}
              >
                {isSubmitting ? (
                  <>
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      ⚡
                    </motion.span>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </motion.button>

              {submitStatus === 'success' && (
                <motion.div
                  className="submit-status success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{ color: '#00ff88' }}
                >
                  ✅ Thank you! Your message has been sent.
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  className="submit-status error"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{ color: '#ff6b6b' }}
                >
                  ❌ Sorry, there was an error sending your message.
                </motion.div>
              )}
            </motion.form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;

